import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Personal dashboard data sources — technical tickets, SLA overview and my
 * workdays. Mirrored from datacentral-client-v4 `modules/Dashboard/services`
 * and `modules/Service/services/sla`. All authorization-gated → UMA RPT client.
 */

/* ── Technical ticket summary (donut counts) ───────────────────────────── */
export interface TicketSummary {
  in_progress_count: number;
  completed_count: number;
  closed_count: number;
  overdue_count: number;
  high_priority_in_progress: number;
  unassigned_count: number;
}

export function useTechnicalTicketSummary() {
  return useQuery({
    queryKey: ['dashboard', 'ticket-summary'],
    queryFn: async (): Promise<TicketSummary | null> => {
      const res = await dataCentralApi.get<ResponseObject<TicketSummary>>(
        '/tickets/technical/summary',
      );
      return res.data?.result ?? null;
    },
  });
}

/* ── Technical ticket list (per active tab) ────────────────────────────── */
export interface TechnicalTicket {
  ticket_id: number;
  ticket_status: string;
  ticket_priority: 'low' | 'medium' | 'high';
  ticket_content: string;
  ticket_requested_at: string;
  ticket_estimated_completion_date: string | null;
  ticket_product_name?: string;
  customer_name?: string;
  product_group_name?: string;
  service_name?: string;
  late_duration_hours?: number | null;
}

/** The list tabs. `unassigned` hits a dedicated endpoint; the rest map to `status`. */
export type TicketTab = 'work-in-progress' | 'completed' | 'closed' | 'unassigned';

export function useTechnicalTickets(tab: TicketTab) {
  return useQuery({
    queryKey: ['dashboard', 'technical-tickets', tab],
    queryFn: async (): Promise<TechnicalTicket[]> => {
      const path =
        tab === 'unassigned'
          ? '/tickets/unassigned?page=1&page_size=10'
          : `/tickets/technical?status=${tab}&page=1&page_size=10`;
      const res = await dataCentralApi.get<ResponseArray<TechnicalTicket>>(path);
      return res.data?.results ?? [];
    },
  });
}

/* ── SLA dashboard overview ─────────────────────────────────────────────── */
export interface SlaOverview {
  total_services_with_sla: number;
  services_meeting_sla: number;
  services_breaching_sla: number;
  avg_uptime_percent: number;
  total_sla_downtime_minutes: number;
  total_all_downtime_minutes: number;
  period_minutes: number;
  total_incidents: number;
  total_provider_downtime: number;
  total_third_party_downtime: number;
  total_scheduled_downtime: number;
}

export function useSlaDashboardOverview(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['dashboard', 'sla-overview', startDate, endDate],
    queryFn: async (): Promise<SlaOverview | null> => {
      const res = await dataCentralApi.get<ResponseObject<SlaOverview>>(
        `/sla/dashboard/overview?start_date=${startDate}&end_date=${endDate}`,
      );
      return res.data?.result ?? null;
    },
  });
}

/* ── My recent workdays ─────────────────────────────────────────────────── */
export interface DayOffInfo {
  day_off_id: number;
  day_off_type: string;
  day_off_status: string;
  day_off_item_option: string;
}
export interface BusinessInfo {
  business_id: number;
  business_type: string;
  business_status: string;
  business_option: string;
}
export interface MyRecentWorkday {
  work_day_id: number;
  work_day_date: string;
  work_day_in: string | null;
  work_day_out: string | null;
  work_day_time: number;
  work_n_day: number;
  work_day_type: string;
  work_day_note: string | null;
  work_day_option: number | null;
  day_off: DayOffInfo | null;
  business: BusinessInfo | null;
}

export function useMyRecentWorkdays(gte: string, lte: string) {
  return useQuery({
    queryKey: ['dashboard', 'my-workdays', gte, lte],
    queryFn: async (): Promise<MyRecentWorkday[]> => {
      const res = await dataCentralApi.get<ResponseArray<MyRecentWorkday>>(
        `/workdays/my-recent?gte=${gte}&lte=${lte}`,
      );
      return res.data?.results ?? [];
    },
    staleTime: 5 * 60_000,
  });
}
